import { useQueryClient } from '@tanstack/react-query';
import { type NotificationItem } from '@utazzo/shared';
import { UzButton, UzPopover, UzTooltip } from '@utazzo/ui';
import { Bell, CheckCheck, Volume2, VolumeX } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  notificationKind,
  notificationTargetPath,
  openAdminRouteInNewTab,
  openNotificationTarget,
} from '../notification-navigation';
import {
  isSoundEnabled,
  playNotificationChime,
  setSoundEnabled,
  warmUpAudio,
} from '../notification-sound';
import * as notifApi from '../notifications.api';
import { useNotificationStream } from '../use-notification-stream';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useMyNotificationsInbox,
} from '../use-notifications';

import { useIsAuthenticated } from '@/features/auth/use-auth';
import { isPushCovering } from '@/features/push/web-push-client';
import { isPhoneDevice } from '@/lib/device';

/**
 * Request browser-level Notification permission once per session.
 * Idempotent - subsequent calls after a grant/deny short-circuit.
 * Falls back to a no-op when the browser doesn't support the API.
 */
const ensureBrowserNotificationPermission = async (): Promise<NotificationPermission> => {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'denied';
  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Notification.permission;
  }
  try {
    return await Notification.requestPermission();
  } catch {
    return 'denied';
  }
};

const showDesktopNotification = (
  title: string,
  body: string | null,
  onClick?: () => void,
): void => {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;
  // This device gets the same alert as a web push (PWA) - showing this one
  // too would double every notification.
  if (isPushCovering('notifications')) return;
  try {
    const n = new Notification(title, {
      body: body ?? undefined,
      // Show even when the tab is focused; the SSE flow guarantees the
      // event is fresh.
      requireInteraction: false,
      // The browser dedupes by tag - useful when multiple rapid events
      // for the same lead would otherwise stack.
      tag: 'utazzo-notification',
    });
    if (onClick) {
      n.onclick = () => {
        onClick();
        n.close();
      };
    }
  } catch {
    // Some browsers throw on certain edge cases (Safari iframe etc.)
    // Silently swallow - the in-app bell still updates.
  }
};

/**
 * In-app notification bell. Two data sources land in the same React
 * Query cache, in priority order:
 *
 *   1. Server-Sent Events stream (ADR-0050). When Redis is up,
 *      `GET /notifications/me/stream` pushes new rows in <1 s. The
 *      stream handler invalidates the inbox query AND fires a
 *      browser-level desktop notification if the operator granted
 *      permission (so they see the alert even when the tab isn't
 *      focused).
 *   2. Polling fallback. The same inbox query refetches every 60 s
 *      regardless of the stream - covers the case where SSE drops
 *      between events. Interval is longer than the pre-SSE 30 s
 *      since the stream now carries the push burden.
 *
 * Both paths land in the same RBAC-scoped /me/inbox response, so the
 * bell never shows another user's rows.
 */
export const InboxBell = (): React.JSX.Element => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isAuthed = useIsAuthenticated();
  const inboxQuery = useMyNotificationsInbox({ limit: 20 }, { refetchInterval: 60_000 });
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  // Local mirror of the sound-on/off preference so the toggle button
  // re-renders on click. Real state lives in localStorage so it
  // survives reload + spreads across tabs.
  const [soundOn, setSoundOn] = useState<boolean>(() => isSoundEnabled());

  // Ask for OS notification permission once per session, lazily on
  // first mount. Doesn't block if denied - the in-app badge still
  // works. Audio warm-up happens on explicit bell / sound-toggle clicks
  // only - passive mount would trigger browser autoplay warnings.
  // Not on phones: iPhone only allows the prompt from a tap (an unprompted
  // request is wasted), and phones switch alerts on from My profile ->
  // Phone notifications instead.
  useEffect(() => {
    if (!isAuthed || isPhoneDevice()) return;
    void ensureBrowserNotificationPermission();
  }, [isAuthed]);

  const toggleSound = (): void => {
    warmUpAudio();
    const next = !soundOn;
    setSoundEnabled(next);
    setSoundOn(next);
    if (next) {
      // Give the operator immediate feedback that they re-enabled it.
      void playNotificationChime();
    }
  };

  // SSE handler. When a new id lands, fetch JUST that notification
  // and show the desktop popup + play the chime. The query invalidation
  // below in the hook takes care of the in-app badge refresh.
  const handlePushed = useCallback(
    async (payload: { id: string; ts: string }) => {
      // 2026-09-06. The chime used to fire before the lookup, on every push.
      // Booking notifications changed that: a confirmed booking is news, not
      // an alarm, and a busy day of them would teach the operator to ignore
      // the bell - which is exactly when the "on hold, act now" one lands.
      // So the row is read first and `info` rows stay silent. If the lookup
      // fails, the chime still fires: better a spare chime than a missed
      // decision.
      let fresh: NotificationItem | undefined;
      try {
        const inbox = await notifApi.getMyNotificationsInbox({ limit: 5 });
        fresh = inbox.data.find((n) => n.id === payload.id);
        if (!fresh) {
          qc.setQueryData(['notifications', 'me', 'inbox', { limit: 20 }], inbox);
        }
      } catch {
        // Failed lookup - just rely on the cache invalidation to catch up.
      }
      if (!fresh || notificationKind(fresh.tags) === 'action') {
        void playNotificationChime();
      }
      if (!fresh) return;
      const target = notificationTargetPath(fresh.tags);
      showDesktopNotification(
        fresh.subject ?? fresh.eventCode,
        fresh.bodyText,
        target ? () => openAdminRouteInNewTab(target) : undefined,
      );
    },
    [qc],
  );

  // The stream's status is exposed for future surfacing (e.g. a small
  // "live"/"offline" dot on the bell). Today we just rely on the
  // polling fallback when the stream isn't open.
  useNotificationStream({ enabled: isAuthed, onNotification: handlePushed });

  const unread = inboxQuery.data?.unreadCount ?? 0;

  // Unread count on the installed app's icon (PWA). Browsers without the
  // Badging API, or a plain tab, simply ignore it.
  const hasInbox = inboxQuery.data !== undefined;
  useEffect(() => {
    if (!hasInbox || typeof navigator === 'undefined' || !('setAppBadge' in navigator)) return;
    const done = unread > 0 ? navigator.setAppBadge(unread) : navigator.clearAppBadge();
    done.catch(() => undefined);
  }, [unread, hasInbox]);
  const items = inboxQuery.data?.data ?? [];
  const badge = unread > 99 ? '99+' : String(unread);

  // Controlled popover so we can close it programmatically after a
  // row click. Without this the dropdown lingers behind the lead
  // detail page the click navigates into, which forces an extra
  // dismiss before the operator can interact with the new page.
  const [open, setOpen] = useState(false);

  return (
    <UzPopover
      open={open}
      onOpenChange={setOpen}
      trigger={
        <button
          type="button"
          onClick={warmUpAudio}
          className="relative inline-flex h-8 w-8 items-center justify-center rounded-[var(--r-md)] text-[var(--fg-muted)] hover:bg-[var(--z-100)] hover:text-[var(--fg)]"
          aria-label={`Notifications (${unread} unread)`}
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 inline-flex min-w-[14px] items-center justify-center rounded-full bg-red-500 px-[3px] text-[9px] font-semibold text-white"
              aria-hidden
            >
              {badge}
            </span>
          )}
        </button>
      }
      align="end"
      className="w-[360px] p-0"
    >
      <div className="flex items-center justify-between border-b border-zinc-200 px-3 py-2">
        <span className="text-sm font-semibold text-zinc-900">Notifications</span>
        <div className="flex items-center gap-0.5">
          <UzTooltip label={soundOn ? 'Mute notification sound' : 'Enable notification sound'}>
            <UzButton variant="ghost" size="sm" onClick={toggleSound}>
              {soundOn ? (
                <Volume2 className="h-4 w-4" />
              ) : (
                <VolumeX className="h-4 w-4 text-zinc-400" />
              )}
            </UzButton>
          </UzTooltip>
          <UzTooltip label="Mark all as read">
            <UzButton
              variant="ghost"
              size="sm"
              onClick={() => markAll.mutate()}
              disabled={markAll.isPending || unread === 0}
            >
              <CheckCheck className="h-4 w-4" />
            </UzButton>
          </UzTooltip>
        </div>
      </div>
      <div className="max-h-[440px] overflow-y-auto">
        {inboxQuery.isLoading ? (
          <div className="px-3 py-6 text-center text-xs text-zinc-500">Loading...</div>
        ) : items.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-zinc-500">You're all caught up.</div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {items.map((n) => (
              <InboxRow
                key={n.id}
                row={n}
                onMarkRead={() => markRead.mutate(n.id)}
                onClose={() => setOpen(false)}
              />
            ))}
          </ul>
        )}
      </div>
      <div className="border-t border-zinc-200 px-3 py-2 text-right">
        <UzButton
          variant="ghost"
          size="sm"
          onClick={() => {
            setOpen(false);
            navigate('/notifications/me');
          }}
        >
          View all
        </UzButton>
      </div>
    </UzPopover>
  );
};

interface InboxRowProps {
  row: NotificationItem;
  onMarkRead: () => void;
  onClose: () => void;
}

const InboxRow = ({ row, onMarkRead, onClose }: InboxRowProps): React.JSX.Element => {
  const unread = row.readAt === null;
  const when = formatRelative(row.createdAt);
  const isLinkable = notificationTargetPath(row.tags) !== null;
  // Booking rows that ask for a decision wear a badge so they stand out in a
  // list that also carries the quiet "booking confirmed" notes.
  const needsAction = Boolean(row.tags?.bookingId) && notificationKind(row.tags) === 'action';

  const handleRowClick = (): void => {
    if (unread) onMarkRead();
    onClose();
    openNotificationTarget(row.tags);
  };

  return (
    <li
      className={`group flex items-start gap-2 px-3 py-2.5 transition-colors ${
        unread ? 'bg-indigo-50/40' : ''
      } ${isLinkable ? 'cursor-pointer hover:bg-zinc-50' : ''}`}
      onClick={handleRowClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleRowClick();
        }
      }}
    >
      <span
        className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${
          unread ? 'bg-indigo-500' : 'bg-transparent'
        }`}
        aria-hidden
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-baseline justify-between gap-2">
          <span className="flex min-w-0 items-baseline gap-1.5">
            <span className="truncate text-xs font-medium text-zinc-900">
              {row.subject ?? row.eventCode}
            </span>
            {needsAction && (
              <span className="shrink-0 rounded-full bg-amber-100 px-1.5 text-[10px] font-semibold text-amber-800">
                Action needed
              </span>
            )}
            {row.occurrenceCount > 1 && (
              <span
                className="shrink-0 rounded-full bg-indigo-100 px-1.5 text-[10px] font-semibold text-indigo-700"
                title={`Occurred ${row.occurrenceCount} times`}
              >
                ×{row.occurrenceCount}
              </span>
            )}
          </span>
          <span className="shrink-0 text-[10px] text-zinc-500">{when}</span>
        </div>
        {row.bodyText && <p className="line-clamp-2 text-[11px] text-zinc-600">{row.bodyText}</p>}
        {/* 2026-07-20. Removed the third-line badge row that surfaced the
            event code + truncated leadId + truncated customerId. Lead
            reference is already in the subject / body for every lead-
            scoped event, so the badges were noise. The row is still
            clickable to open the lead (handleRowClick above resolves
            the target from `row.tags`). */}
      </div>
      {unread && (
        <UzButton
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onMarkRead();
          }}
          title="Mark read"
        >
          <CheckCheck className="h-3.5 w-3.5" />
        </UzButton>
      )}
    </li>
  );
};

const formatRelative = (iso: string): string => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
  });
};
